import {useEffect, useRef, useState} from 'react'
import {
  Box,
  Center,
  Flex,
  Grid,
  GridItem,
  Image,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
  Skeleton,
  SkeletonText,
} from '@chakra-ui/react'
import {useLocation, useNavigate} from 'react-router-dom'
import AvatarFull from '@assets/images/learn/avatar_full_body_opacity.png'
import {FEATURE_NAME, PATH, SCREEN_TRACKING} from '@utils/Const'
import LockIcon from '@assets/svgs/roadmap/lock.svg'
import {
  updateEntryPointShowUpgradeModal,
  updateIsShowUpgradeModal,
} from '@redux/map/mapSlice'
import {useDispatch} from 'react-redux'
import {isParamsValid, trackingPage} from '@utils'
import {CommonColors} from '@utils/CommonStyles'
import {ReactComponent as SearchIcon} from '@assets/svgs/search.svg'
import appRequest from '@apis/app'
import {ReactComponent as CheckIcon} from '@assets/svgs/check.svg'
import GlobalButton from '@components/GlobalButton/GlobalButton'

function LessonPage() {
  const location = useLocation()
  const {feature, thumbnail, topic, title} = location.state || {}
  const [lessonData, setLessonData] = useState<any>(null)
  const [filteredData, setFilteredData] = useState<any>(null)
  const [currentTab, setCurrentTab] = useState<any>(topic)
  const [search, setSearch] = useState<string>('')
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const observerRef = useRef<IntersectionObserver | null>(null)
  const [isManualTabChange, setIsManualTabChange] = useState(true)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!isParamsValid([feature])) {
      navigate(PATH.HOME)
    }
  }, [])

  useEffect(() => {
    const getTopicDetails = async () => {
      try {
        const params = {
          feature: feature,
        }
        const res = await appRequest.getTopicDetail(params)
        setLessonData(res?.data?.data)
      } catch (error) {
      } finally {
        setIsLoading(false)
      }
    }
    getTopicDetails()
  }, [])

  useEffect(() => {
    if (topic) return
    setCurrentTab(lessonData?.content[0]?.topic)
  }, [lessonData])

  useEffect(() => {
    if (!lessonData) return

    if (!search.trim()) {
      setFilteredData(lessonData)
      return
    }

    const searchLower = search.toLowerCase()
    const filtered = {
      ...lessonData,
      content: lessonData.content
        .map((topic: any) => ({
          ...topic,
          questions: topic.questions.filter((question: any) =>
            question.text_en.toLowerCase().includes(searchLower),
          ),
        }))
        .filter((topic: any) => topic.questions.length > 0),
    }

    setFilteredData(filtered)
    if (filtered?.content?.length > 0) {
      setCurrentTab(filtered?.content[0]?.topic)
    } else {
      setCurrentTab(null)
    }
  }, [search, lessonData])

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !isManualTabChange) {
            const topic = entry.target.getAttribute('data-topic-name')
            if (topic) {
              setCurrentTab(topic)
            }
          }
        })
      },
      {
        threshold: 0,
        root: null,
        rootMargin: '-70px 0px -100% 0px',
      },
    )

    document.querySelectorAll('[data-topic-name]').forEach(element => {
      observerRef.current?.observe(element)
    })

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [filteredData, isManualTabChange])

  useEffect(() => {
    if (!currentTab || !isManualTabChange || !filteredData?.content?.length)
      return

    const topicExists = filteredData.content.some(
      (topic: any) => topic.topic === currentTab,
    )

    if (!topicExists) {
      setIsManualTabChange(false)
      return
    }

    observerRef.current?.disconnect()

    const topicElement = document.querySelector(
      `[data-topic-name="${currentTab}"]`,
    )

    if (topicElement) {
      const elementPosition = topicElement.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - 70

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })

      setTimeout(() => {
        setIsManualTabChange(false)
        document.querySelectorAll('[data-topic-name]').forEach(element => {
          observerRef.current?.observe(element)
        })
      }, 1000)
    } else {
      setIsManualTabChange(false)
      console.log('Topic element not found for:', currentTab)
    }
  }, [currentTab, filteredData])

  useEffect(() => {
    if (!currentTab || !filteredData) return

    const tabElement = document.querySelector(
      `[role="tab"][data-topic="${currentTab}"]`,
    )

    if (tabElement) {
      setTimeout(() => {
        tabElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        })
      }, 100)
    }
  }, [currentTab, filteredData])

  const getTitle = (feature: string) => {
    switch (feature) {
      case 'LESSON_PART_ONE':
        return 'Part 1'
      case 'LESSON_PART_TWO_NEW':
        return 'Part 2'
      case 'LESSON_PART_THREE':
        return 'Part 3'
      case 'FORECAST_SET':
        return title
    }
  }

  const handleClickQuestion = (lesson: any) => {
    if (lesson.is_lock) {
      trackingPage(SCREEN_TRACKING.SALE_SCREEN_VIEW, {
        entry_point: 'learn_page',
      })
      dispatch(updateIsShowUpgradeModal(true))
      dispatch(updateEntryPointShowUpgradeModal('learn_page'))
      return
    }

    const navigationConfig = {
      LESSON_PART_ONE: {
        path: PATH.CONVERSATION,
        state: {
          lesson_id: lesson?.lesson_id,
          lesson_name: lesson?.text_en,
          feature_name: FEATURE_NAME.LESSON_PART_ONE,
        },
      },
      LESSON_PART_TWO_NEW: {
        path: PATH.LEARN_PART_TWO,
        state: {lesson_id: lesson?.lesson_id},
      },
      LESSON_PART_THREE: {
        path: PATH.LEARN_PART_THREE,
        state: {lesson_id: lesson?.lesson_id},
      },
      VOCABULARY: {
        path: PATH.LEARN_VOCABULARY_OVERVIEW,
        state: {setId: lesson?.set_id},
      },
    }

    const lessonFeature = feature === 'FORECAST_SET' ? lesson?.feature : feature
    const config =
      navigationConfig[lessonFeature as keyof typeof navigationConfig]

    if (config) {
      navigate(config.path, {state: config.state})
    }
  }

  const checkQuestionIsStart = (topicItem: any, question: any) => {
    if (question?.is_done) return false
    if (topic) {
      if (topicItem.topic === topic) {
        const firstUndoneIndex = topicItem.questions.findIndex(
          (q: any) => !q.is_done,
        )
        const currentQuestionIndex = topicItem.questions.findIndex(
          (q: any) => q === question,
        )
        return firstUndoneIndex === currentQuestionIndex
      }
      return false
    } else {
      const allQuestions =
        lessonData?.content.flatMap((t: any) => t.questions) || []
      const firstUndoneQuestion = allQuestions.find((q: any) => !q.is_done)
      return question === firstUndoneQuestion
    }
  }

  const handleTabClick = (topic: string) => {
    setIsManualTabChange(true)
    setCurrentTab(topic)
  }

  const renderHeader = () => (
    <Box bg={CommonColors.white} p={{base: '16px', md: '32px'}}>
      <Box maxW={'1400px'} mx={'auto'}>
        <Flex alignItems={'center'} gap={{base: '16px', md: '24px'}}>
          <Image
            flexShrink={0}
            w={{base: '100px', md: '160px'}}
            h={{base: '100px', md: '160px'}}
            src={thumbnail ?? AvatarFull}
            borderRadius={'16px'}
          />
          <Flex flexDirection={'column'} gap={{base: '8px', md: '16px'}}>
            <Text
              fontSize={{base: '20px', md: '24px'}}
              fontWeight={'600'}
              color={'#7C7C7C'}>
              Luyện tập theo chủ đề
            </Text>
            <Text fontSize={{base: '24px', md: '30px'}} fontWeight={'600'}>
              {getTitle(feature)}
            </Text>
            <Text
              fontSize={{base: '14px', md: '16px'}}
              fontWeight={'600'}
              color={CommonColors.primary}>
              {feature === 'FORECAST_SET'
                ? `Tất cả các câu hỏi theo bộ đề ${title}`
                : `Tất cả các câu hỏi ${getTitle(feature)} theo chủ đề`}
            </Text>
          </Flex>
        </Flex>
      </Box>
    </Box>
  )

  const renderTopicTab = () => (
    <Box
      zIndex={1}
      position={'sticky'}
      top={'0'}
      px={{base: '16px', md: '32px'}}
      bg={CommonColors.white}>
      <Box
        maxW={'1400px'}
        mx={'auto'}
        className="scroll-bar"
        overflowX={'auto'}
        whiteSpace={'nowrap'}
        sx={{
          '&::-webkit-scrollbar': {
            height: '4px', // Chiều cao của thanh scroll
          },
        }}>
        <Flex>
          {filteredData?.content?.map((topic: any, index: number) => (
            <Center
              key={index}
              role="tab"
              data-topic={topic?.topic}
              w={'fit-content'}
              h={'50px'}
              px={{base: '16px', md: '24px'}}
              flexShrink={0}
              cursor={'pointer'}
              borderRadius={'8px 8px 0 0'}
              bg={currentTab === topic?.topic ? '#F5F5F5' : 'transparent'}
              onClick={() => handleTabClick(topic?.topic)}>
              <Text
                fontSize={'16px'}
                fontWeight={currentTab === topic?.topic ? '700' : '400'}
                color={
                  currentTab === topic?.topic ? CommonColors.primary : '#7C7C7C'
                }>
                {topic?.topic}
              </Text>
            </Center>
          ))}
        </Flex>
      </Box>
    </Box>
  )

  const renderSearch = () => (
    <Flex justifyContent={'flex-end'}>
      <InputGroup
        border={'1px solid #DCDCDC'}
        borderRadius={'8px'}
        w={{base: '100%', sm: '300px'}}>
        <InputLeftElement>
          <SearchIcon color={'#7C7C7C'} />
        </InputLeftElement>
        <Input
          placeholder="Tìm kiếm"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </InputGroup>
    </Flex>
  )

  const renderLoadingSkeleton = () => (
    <Box bg={'white'} p={{base: '16px', md: '24px'}} borderRadius={'16px'}>
      <Skeleton height="32px" width="200px" />
      <Box h={'1px'} bg={'#EFEFEF'} my={{base: '16px', md: '24px'}} />
      <Grid
        templateColumns={{base: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)'}}
        gap={{base: '16px', md: '24px'}}>
        {[...Array(6)].map((_, index) => (
          <GridItem key={index}>
            <Flex
              borderRadius={'16px'}
              border="2px solid #f3f3f3"
              p={{base: '16px', md: '20px'}}
              gap={'16px'}
              h={'100%'}>
              <Skeleton
                flexShrink={0}
                w={'40px'}
                h={'40px'}
                borderRadius={'50%'}
              />
              <Flex flexDirection={'column'} gap={'12px'} flex={1}>
                <SkeletonText noOfLines={2} spacing="2" />
                <SkeletonText noOfLines={1} width="60%" />
              </Flex>
            </Flex>
          </GridItem>
        ))}
      </Grid>
    </Box>
  )

  const renderContent = () => (
    <Flex
      flexDirection={'column'}
      mt={{base: '16px', md: '32px'}}
      gap={{base: '16px', md: '32px'}}>
      {isLoading ? (
        renderLoadingSkeleton()
      ) : filteredData?.content?.length > 0 ? (
        filteredData?.content?.map((topic: any, index: number) => (
          <Box
            key={index}
            bg={'white'}
            p={{base: '16px', md: '24px'}}
            borderRadius={'16px'}
            data-topic-name={topic.topic}>
            <Text fontSize={{base: '18px', md: '24px'}} fontWeight={'600'}>
              {topic.topic}
            </Text>
            <Box h={'1px'} bg={'#EFEFEF'} my={{base: '16px', md: '24px'}} />
            <Grid
              templateColumns={{base: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)'}}
              gap={{base: '16px', md: '24px'}}>
              {topic?.questions?.map((question: any, index: number) => (
                <GridItem
                  key={index}
                  h={'100%'}
                  cursor={'pointer'}
                  onClick={() => handleClickQuestion(question)}>
                  <Flex
                    borderRadius={'16px'}
                    border="2px solid #f3f3f3"
                    p={{base: '16px', md: '20px'}}
                    gap={'16px'}
                    h={'100%'}
                    _hover={{
                      border: '2px solid #8ED4FF',
                      boxShadow: '0px 4px 0px 0px #8ED4FF',
                    }}>
                    <Center
                      flexShrink={0}
                      w={'40px'}
                      h={'40px'}
                      border={'1px solid #DCDCDC'}
                      borderRadius={'50%'}
                      overflow={'hidden'}>
                      <Image w={'100%'} h={'100%'} src={AvatarFull} />
                    </Center>
                    <Flex flexDirection={'column'} gap={'12px'} flex={1}>
                      <Text fontSize={'16px'} fontWeight={'600'}>
                        {question.text_en}
                      </Text>
                      <Text fontSize={'12px'} color={'#7C7C7C'}>
                        {question.text_vi}
                      </Text>
                    </Flex>
                    {question?.is_done ? (
                      <Center
                        w={'24px'}
                        h={'24px'}
                        bg={'#ECFDF3'}
                        borderRadius={'50%'}
                        border={'1px solid #DCFAE6'}>
                        <Icon
                          as={CheckIcon}
                          w={'16px'}
                          h={'16px'}
                          flexShrink={0}
                          sx={{
                            path: {
                              stroke: '#17B26A',
                            },
                          }}
                        />
                      </Center>
                    ) : (
                      checkQuestionIsStart(topic, question) && (
                        <GlobalButton
                          variant={'outline'}
                          fontSize={'14px'}
                          fontWeight={'600'}
                          w="84px"
                          h="36px"
                          borderRadius={'12px'}>
                          Bắt đầu
                        </GlobalButton>
                      )
                    )}
                  </Flex>
                </GridItem>
              ))}
            </Grid>
          </Box>
        ))
      ) : (
        <Text>Không tìm thấy kết quả</Text>
      )}
    </Flex>
  )

  return (
    <Box pt={{base: '16px', md: 0}}>
      {renderHeader()}
      {renderTopicTab()}
      <Box p={{base: '16px', md: '32px'}}>
        <Box maxW={'1400px'} mx={'auto'}>
          {renderSearch()}
          {renderContent()}
        </Box>
      </Box>
    </Box>
  )
}

export default LessonPage
